import { Suspense, useEffect, useState, type FC } from 'react';
import ReactDOM from 'react-dom';
import styled, { keyframes } from 'styled-components';
import TimesIcon from '../../assets/TimesIcon.svg';
import {
  type LeaderboardPagedResults,
  LeaderboardTypeParam,
  useLeaderboardQuery,
} from '../../apiHooks/useLeaderboardQuery';
import type { LeaderboardUser, LeaderboardDepartment } from '../../lib/types/LeaderboardUser';
import { UserLevelDetails } from '../../lib/enums/userLevels';
import { formatNumberWithCommas } from '../../lib/numbers/formatNumberWithComma';
import { AvatarInitials } from '../AvatarInitials';
import { Icon, IconType } from '../Props/Icon/Icon';
import { NamedContentEntryText } from '../ContentEntryText/NamedContentEntryText';
import { Spinner } from '../Spinner/Spinner';
import { useMeQuery } from '../../apiHooks/useMeQuery';
import { useEscapeKeyHandler } from '../../lib/hooks/useEscapeKeyHandler';
import { withErrorBoundaries } from '../../lib/decorators/withErrorBoundaries';
import { CardErrorView } from '../ErrorView/CardErrorView';
import { getRankArrow } from './components/UserRank/utils/getRankArrow';
import { useCurrentUserDepartment } from './hooks/useCurrentUserDepartment';

const modalRoot = document.getElementById('modal');

// Podium display order: 2nd place, 1st place, 3rd place. Indexed by finishing place (0 = 1st).
const PODIUM_ORDER = [1, 0, 2];
const STEP_HEIGHT = [128, 92, 76];
const MEDAL_RING = ['#FFC97A', '#DFE4EE', '#F3B24B'];

type LeaderboardExpandedProps = {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: LeaderboardTypeParam;
  isManager?: boolean;
};

const normalizeData = (
  data:
    | (LeaderboardUser | LeaderboardDepartment)[]
    | LeaderboardPagedResults<LeaderboardUser | LeaderboardDepartment>
    | undefined,
): (LeaderboardUser | LeaderboardDepartment)[] => {
  if (Array.isArray(data)) {
    return data;
  }
  return data?.values || [];
};

export const LeaderboardExpanded: FC<LeaderboardExpandedProps> = ({
  isOpen,
  onClose,
  initialTab = LeaderboardTypeParam.TEAM,
  isManager,
}) => {
  const [tab, setTab] = useState<LeaderboardTypeParam>(initialTab);

  useEffect(() => {
    if (isOpen) {
      setTab(initialTab);
    }
  }, [isOpen, initialTab]);

  useEscapeKeyHandler({ active: isOpen, onEscapePressed: onClose });

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!isOpen || !modalRoot) {
    return null;
  }

  const tabs: LeaderboardTypeParam[] = [
    LeaderboardTypeParam.TEAM,
    LeaderboardTypeParam.DEPARTMENT,
    LeaderboardTypeParam.COMPANY,
  ];

  const overlay = (
    <Overlay>
      <TopBar>
        <TopBarLabel>
          <NamedContentEntryText container="span" refKey="sectionTitles" subKey="leaderboard" />
        </TopBarLabel>
        <TabToggle>
          {tabs.map((t) => (
            <TabButton key={t} type="button" $active={tab === t} onClick={() => setTab(t)}>
              {t === LeaderboardTypeParam.TEAM ? (
                <NamedContentEntryText container="span" refKey="leaderboard" subKey="myTeam" />
              ) : t === LeaderboardTypeParam.DEPARTMENT ? (
                <NamedContentEntryText container="span" refKey="leaderboard" subKey="myDepartment" />
              ) : (
                <NamedContentEntryText container="span" refKey="leaderboard" subKey="myCompany" />
              )}
            </TabButton>
          ))}
        </TabToggle>
        <CloseButton type="button" aria-label="Close" onClick={onClose}>
          <TimesIcon />
        </CloseButton>
      </TopBar>

      <ContentArea>
        <Suspense
          fallback={
            <Centered>
              <Spinner size="32px" color="#ffffff" />
            </Centered>
          }
        >
          <LeaderboardExpandedContent_WithErrorBoundary tab={tab} isManager={isManager} />
        </Suspense>
      </ContentArea>
    </Overlay>
  );

  return ReactDOM.createPortal(overlay, modalRoot);
};

type LeaderboardExpandedContentProps = {
  tab: LeaderboardTypeParam;
  isManager?: boolean;
};

const LeaderboardExpandedContent: FC<LeaderboardExpandedContentProps> = ({ tab, isManager }) => {
  const isDept = tab === LeaderboardTypeParam.DEPARTMENT;
  const { data: meData } = useMeQuery();
  const currentUserDepartment = useCurrentUserDepartment();

  const { data } = useLeaderboardQuery({
    type: tab,
    mode: 'top',
    count: 100,
    isManager,
  });

  const entries = normalizeData(data);
  const topThree = entries.slice(0, 3);

  const you = isDept
    ? null
    : ((entries as LeaderboardUser[]).find((entry) => entry.learnerId === meData?.learnerId) ?? null);

  const isCurrentRow = (entry: LeaderboardUser | LeaderboardDepartment): boolean => {
    if ('learnerId' in entry) {
      return entry.learnerId === meData?.learnerId;
    }
    if ('department' in entry && currentUserDepartment) {
      return entry.department === currentUserDepartment;
    }
    return false;
  };

  return (
    <>
      <Scroll>
        <Inner>
          <Podium>
            <PodiumHeading>
              <NamedContentEntryText container="span" refKey="sectionTitles" subKey="leaderboard" />
            </PodiumHeading>
            <PodiumRow>
              {PODIUM_ORDER.map((place) => {
                const entry = topThree[place];
                if (!entry) {
                  return null;
                }
                if (isDept) {
                  const dept = entry as LeaderboardDepartment;
                  return (
                    <PodiumCol
                      key={place}
                      place={place}
                      rank={dept.rank}
                      name={dept.department}
                      points={dept.averageScore}
                      isDept
                    />
                  );
                }
                const user = entry as LeaderboardUser;
                return (
                  <PodiumCol
                    key={place}
                    place={place}
                    rank={user.rank}
                    name={user.name}
                    points={user.cyberIqPoints}
                    avatar={user.avatar}
                    isYou={user.learnerId === meData?.learnerId}
                  />
                );
              })}
            </PodiumRow>
          </Podium>

          <ListCard>
            <ListScroll>
              <ListInner>
                <ListHeader $isDept={isDept}>
                  <span>#</span>
                  <span />
                  {isDept ? (
                    <>
                      <span>
                        <NamedContentEntryText container="span" refKey="leaderboard" subKey="departments" />
                      </span>
                      <span>
                        <NamedContentEntryText container="span" refKey="managerTeamBehaviors" subKey="teamSize" />
                      </span>
                      <AlignRight>
                        <NamedContentEntryText container="span" refKey="leaderboard" subKey="points" />
                      </AlignRight>
                    </>
                  ) : (
                    <>
                      <span>
                        <NamedContentEntryText container="span" refKey="leaderboard" subKey="name" />
                      </span>
                      <span>
                        <NamedContentEntryText container="span" refKey="leaderboard" subKey="level" />
                      </span>
                      <AlignRight>
                        <NamedContentEntryText container="span" refKey="leaderboard" subKey="points" />
                      </AlignRight>
                    </>
                  )}
                </ListHeader>

                {isDept
                  ? (entries as LeaderboardDepartment[]).map((dept) => (
                      <DeptRow key={dept.rank} dept={dept} highlighted={isCurrentRow(dept)} />
                    ))
                  : (entries as LeaderboardUser[]).map((user) => (
                      <PersonRow key={user.learnerId} user={user} highlighted={isCurrentRow(user)} />
                    ))}
              </ListInner>
            </ListScroll>
          </ListCard>
        </Inner>
      </Scroll>

      {you && (
        <YouBar>
          <YouPill>
            <YouRank>{you.rank}</YouRank>
            {you.avatar ? (
              <RowAvatarImg src={`data:image/png;base64,${you.avatar}`} alt="" />
            ) : (
              <AvatarInitials name={you.name || ''} id={you.learnerId} size="28px" />
            )}
            <YouName>{you.name}</YouName>
            <LevelPill levelId={you.levelId} />
            <PointsCell>
              <Icon width={16} height={16} icon={IconType.COIN} />
              {formatNumberWithCommas(you.cyberIqPoints)}
            </PointsCell>
          </YouPill>
        </YouBar>
      )}
    </>
  );
};

const { LeaderboardExpandedContent_WithErrorBoundary } = withErrorBoundaries(
  { LeaderboardExpandedContent },
  CardErrorView,
);

type PodiumColProps = {
  place: number;
  rank: number;
  name: string;
  points: number;
  avatar?: string | null;
  isYou?: boolean;
  isDept?: boolean;
};

const PodiumCol: FC<PodiumColProps> = ({ place, rank, name, points, avatar, isYou, isDept }) => {
  const big = place === 0;
  return (
    <PodiumColRoot>
      <PodiumMeta>
        <PodiumAvatarRing $big={big} style={{ background: MEDAL_RING[place] }}>
          {isDept ? (
            <AvatarInitials name={name} id={name} size={big ? '60px' : '44px'} />
          ) : avatar ? (
            <PodiumAvatarImg $big={big} src={`data:image/png;base64,${avatar}`} alt="" />
          ) : (
            <AvatarInitials name={name} id={isYou ? `you-${name}` : name} size={big ? '60px' : '44px'} />
          )}
        </PodiumAvatarRing>
        <PodiumName>{name}</PodiumName>
        <PodiumPoints>
          <Icon width={14} height={14} icon={IconType.COIN} />
          {formatNumberWithCommas(points)}
        </PodiumPoints>
      </PodiumMeta>
      <PodiumStep style={{ height: STEP_HEIGHT[place] }}>
        <PodiumStepRank>{rank}</PodiumStepRank>
      </PodiumStep>
    </PodiumColRoot>
  );
};

const LevelPill: FC<{ levelId: LeaderboardUser['levelId'] }> = ({ levelId }) => {
  const details = UserLevelDetails[levelId];
  if (!details) {
    return null;
  }
  return (
    <LevelPillRoot>
      <LevelDot style={{ background: details.color }} />
      {details.name}
    </LevelPillRoot>
  );
};

const PersonRow: FC<{ user: LeaderboardUser; highlighted: boolean }> = ({ user, highlighted }) => (
  <Row $isDept={false} $highlighted={highlighted}>
    <RankCell>{user.rank}</RankCell>
    <TrendCell>{getRankArrow(user.rank, user.previousRank)}</TrendCell>
    <NameCell>
      {user.avatar ? (
        <RowAvatarImg src={`data:image/png;base64,${user.avatar}`} alt="" />
      ) : (
        <AvatarInitials name={user.name || ''} id={user.learnerId} size="28px" />
      )}
      <Ellipsize>{user.name}</Ellipsize>
    </NameCell>
    <LevelPill levelId={user.levelId} />
    <PointsCell>
      <Icon width={16} height={16} icon={IconType.COIN} />
      {formatNumberWithCommas(user.cyberIqPoints)}
    </PointsCell>
  </Row>
);

const DeptRow: FC<{ dept: LeaderboardDepartment; highlighted: boolean }> = ({ dept, highlighted }) => (
  <Row $isDept $highlighted={highlighted}>
    <RankCell>{dept.rank}</RankCell>
    <TrendCell>{getRankArrow(dept.rank, dept.previousRank)}</TrendCell>
    <NameCell>
      <AvatarInitials name={dept.department} id={dept.department} size="28px" />
      <Ellipsize>{dept.department}</Ellipsize>
    </NameCell>
    <MembersCell>{formatNumberWithCommas(dept.learnerCount)}</MembersCell>
    <PointsCell>
      <Icon width={16} height={16} icon={IconType.COIN} />
      {formatNumberWithCommas(dept.averageScore)}
    </PointsCell>
  </Row>
);

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const PEOPLE_GRID = '40px 28px minmax(150px, 1fr) 220px 110px';
const DEPT_GRID = '40px 28px minmax(190px, 1fr) 120px 120px';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  color: #ffffff;
  animation: ${fadeIn} 0.2s ease;
  background: radial-gradient(120% 120% at 50% 0%, hsl(89 25% 38%) 0%, hsl(89 29% 22%) 55%, hsl(89 31% 12%) 100%);
`;
const TopBar = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 32px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 14px 20px;
  }
`;

const TopBarLabel = styled.span`
  width: 96px;
  font-size: ${({ theme }) => theme.fontSizes.normal};
  font-weight: 500;
  color: rgba(255, 255, 255, 0.6);
  letter-spacing: 0.6px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const TabToggle = styled.div`
  display: inline-flex;
  padding: 4px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.1);
  margin: 0 auto;

  @media (max-width: 768px) {
    margin: 0;
  }
`;

const TabButton = styled.button<{ $active: boolean }>`
  padding: 6px 14px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSizes.normal};
  font-weight: 500;
  transition:
    background 0.15s ease,
    color 0.15s ease;
  background: ${({ $active }) => ($active ? '#ffffff' : 'transparent')};
  color: ${({ $active, theme }) => ($active ? theme.colors.prototype.foreground : 'rgba(255, 255, 255, 0.7)')};

  span {
    color: inherit;
  }
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  svg {
    width: 16px;
    height: 16px;
  }

  svg path {
    fill: #ffffff;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const Centered = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Scroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

const Inner = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: 8px 32px 40px;

  @media (max-width: 768px) {
    padding: 8px 20px 32px;
  }
`;

const Podium = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 0 32px;
`;

const PodiumHeading = styled.span`
  margin-bottom: 20px;
  font-size: ${({ theme }) => theme.fontSizes.small};
  text-transform: uppercase;
  letter-spacing: 2px;
  color: rgba(255, 255, 255, 0.55);
`;

const PodiumRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 24px;

  @media (max-width: 768px) {
    gap: 12px;
  }
`;

const PodiumColRoot = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
`;

const PodiumMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
`;

const PodiumAvatarRing = styled.div<{ $big: boolean }>`
  width: ${({ $big }) => ($big ? '64px' : '48px')};
  height: ${({ $big }) => ($big ? '64px' : '48px')};
  padding: 2.5px;
  border-radius: 50%;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.35);
`;

const PodiumAvatarImg = styled.img<{ $big: boolean }>`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
`;

const PodiumName = styled.span`
  max-width: 100px;
  font-size: ${({ theme }) => theme.fontSizes.small};
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PodiumPoints = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.2);
  font-size: ${({ theme }) => theme.fontSizes.small};
  font-weight: 600;
`;

const PodiumStep = styled.div`
  width: 96px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 8px;
  border-radius: 16px 16px 0 0;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.45), rgba(255, 255, 255, 0.12));

  @media (max-width: 768px) {
    width: 76px;
  }
`;

const PodiumStepRank = styled.span`
  font-size: 34px;
  font-weight: 800;
  line-height: 1;
  color: rgba(255, 255, 255, 0.9);
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const ListCard = styled.div`
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.06);
  overflow: hidden;
`;

const ListScroll = styled.div`
  overflow-x: auto;
`;

const ListInner = styled.div`
  min-width: 560px;

  @media (min-width: 768px) {
    min-width: 100%;
  }
`;

const ListHeader = styled.div<{ $isDept: boolean }>`
  display: grid;
  grid-template-columns: ${({ $isDept }) => ($isDept ? DEPT_GRID : PEOPLE_GRID)};
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  font-size: ${({ theme }) => theme.fontSizes.small};
  text-transform: uppercase;
  letter-spacing: 1px;
  color: rgba(255, 255, 255, 0.5);

  span {
    color: inherit;
  }
`;

const AlignRight = styled.span`
  text-align: right;
`;

const Row = styled.div<{ $isDept: boolean; $highlighted: boolean }>`
  display: grid;
  grid-template-columns: ${({ $isDept }) => ($isDept ? DEPT_GRID : PEOPLE_GRID)};
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  transition: background 0.15s ease;
  background: ${({ $highlighted }) => ($highlighted ? 'rgba(255, 255, 255, 0.12)' : 'transparent')};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${({ $highlighted }) => ($highlighted ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 255, 255, 0.05)')};
  }
`;

const RankCell = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.normal};
  color: rgba(255, 255, 255, 0.55);
`;

const TrendCell = styled.span`
  display: flex;
  align-items: center;

  svg {
    width: 14px;
    height: 14px;
  }
`;

const NameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-size: ${({ theme }) => theme.fontSizes.normal};
`;

const Ellipsize = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MembersCell = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.normal};
  color: rgba(255, 255, 255, 0.55);
`;

const PointsCell = styled.span`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  font-size: ${({ theme }) => theme.fontSizes.normal};
  font-weight: 500;
`;

const LevelPillRoot = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.1);
  font-size: ${({ theme }) => theme.fontSizes.small};
  white-space: nowrap;

  span {
    color: inherit;
  }
`;

const LevelDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const RowAvatarImg = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

const YouBar = styled.div`
  flex-shrink: 0;
  padding: 8px 32px 20px;

  @media (max-width: 768px) {
    padding: 8px 20px 16px;
  }
`;

const YouPill = styled.div`
  max-width: 960px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(6px);
`;

const YouRank = styled.span`
  width: 28px;
  flex-shrink: 0;
  font-size: ${({ theme }) => theme.fontSizes.normal};
  font-weight: 700;
`;

const YouName = styled.span`
  flex: 1;
  min-width: 0;
  font-size: ${({ theme }) => theme.fontSizes.normal};
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
